import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import React, { useEffect, useState } from "react";

import { BulbIcon } from "@/app/assets/icons/interface/interface";
import Spinner from "@/app/components/Spinner";

interface LangContent {
	en: React.ReactNode;
	sw: React.ReactNode;
}

/**
 * Flag assets for the language toggle, one per language the explainer speaks.
 *
 * Files rather than emoji: Windows ships no flag glyphs, so 🇬🇧 and 🇹🇿 render there as the bare
 * letters "GB" and "TZ" — the very cue the flag was meant to replace.
 */
const LANGUAGE_FLAGS = {
	en: { src: "/images/flags/gb.svg", label: "English" },
	sw: { src: "/images/flags/tz.svg", label: "Kiswahili" },
} as const;

interface RoleExplainerAccordionProps {
	title: string;
	content: LangContent;
	loading?: boolean;
	lang?: "en" | "sw";
	onLangChange?: (lang: "en" | "sw") => void;
	/** Fires whenever the accordion opens or closes, and once on mount with the starting state. */
	onExpandedChange?: (expanded: boolean) => void;
}

function RoleExplainerAccordion({
	title,
	content,
	loading,
	lang: externalLang,
	onLangChange,
	onExpandedChange,
}: RoleExplainerAccordionProps) {
	// start closed if loading, otherwise open
	const [open, setOpen] = useState<boolean>(() => !loading);
	const [internalLang, setInternalLang] = useState<"en" | "sw">("en");

	// Use external lang if provided, otherwise use internal state
	const lang = externalLang ?? internalLang;

	// keep accordion closed while loading and open automatically when loading completes
	useEffect(() => {
		if (typeof loading === "boolean") setOpen(!loading);
	}, [loading]);

	// Report the state rather than only the toggle, so the host stays in step with the chevron,
	// the content area and the automatic open above without having to guess which one moved it.
	useEffect(() => {
		onExpandedChange?.(open);
	}, [open, onExpandedChange]);

	const toggleOpen = () => setOpen((v) => !v);

	const handleLangToggle = (e: React.MouseEvent, next?: "en" | "sw") => {
		e.stopPropagation(); // prevent accordion toggle
		const newLang = next ?? (lang === "en" ? "sw" : "en");
		if (onLangChange) {
			onLangChange(newLang);
		} else {
			setInternalLang(newLang);
		}
	};

	return (
		<div
			className="w-full border rounded-lg border-[#C9DBF0] shadow-xs"
			style={{ background: "#EFF6FF", borderColor: "#E6F0FF" }}
		>
			{/* Top header bar */}
			<div
				className="flex items-center justify-between px-3 py-2 rounded-t-lg"
				style={{ background: "#E6F2FF", color: "#0B4F9E" }}
			>
				<div className="flex items-center gap-2">
					<span className="text-lg">
						<BulbIcon />
					</span>
					<span className="font-medium text-sm flex items-center gap-2">
						{title}
						{loading && <Spinner />}
					</span>
				</div>

				<div className="flex items-center gap-4">
					{/* Language switch: EN and SW either side of a rail whose knob shows the live flag. */}
					<div className="select-none">
						<button
							type="button"
							role="switch"
							aria-checked={lang === "sw"}
							onClick={(e) => handleLangToggle(e)}
							// No outer pill: the white rail the knob rides on is the only track needed, and a
							// second one around the whole control just boxed in the labels.
							className="h-6 flex items-center gap-1 relative focus:outline-hidden"
							aria-label="Toggle language"
						>
							{/* Both labels stay readable now, with the inactive one dimmed — the flag says which
							    language is live, so hiding the other label only cost the reader the choice. */}
							<span className={`text-[14px] font-medium ${lang === "en" ? "text-[#0B4F9E]" : "text-[#8FA4C0]"}`}>
								EN
							</span>

							{/* Rail the knob slides along, so the flag lands beside whichever label is active. */}
							<span className="relative h-4 w-7 rounded-full bg-white" aria-hidden>
								<span
									className={`absolute top-0 h-4 w-4 rounded-full overflow-hidden shadow transition-all duration-200 ${
										lang === "sw" ? "left-3" : "left-0"
									}`}
								>
									{/* Decorative: the EN/SW labels already name the languages. object-cover fills the
									    round knob with a flag whose own aspect ratio is wider. */}
									<img src={LANGUAGE_FLAGS[lang].src} alt="" className="h-full w-full object-cover" />
								</span>
							</span>

							<span className={`text-[14px] font-medium ${lang === "sw" ? "text-[#0B4F9E]" : "text-[#8FA4C0]"}`}>
								SW
							</span>
						</button>
					</div>

					{/* Chevron button controls accordion open/close */}
					<button
						type="button"
						onClick={toggleOpen}
						aria-expanded={open}
						aria-disabled={loading}
						className={`p-1 rounded-full hover:bg-[#DCEFFD] transition-transform ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
						style={{ color: "#0B4F9E" }}
						disabled={!!loading}
					>
						<ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
					</button>
				</div>
			</div>

			{/* Accordion (controlled) */}
			<Accordion.Root
				type="single"
				collapsible
				value={open ? "item-1" : undefined}
				onValueChange={(v) => setOpen(Boolean(v))}
			>
				<Accordion.Item value="item-1" className="rounded-b-lg overflow-hidden">
					<Accordion.Content
						className="text-[#2B2B2B] text-sm leading-snug px-2 py-2 space-y-3 data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown"
						style={{ background: "#F8FAFF", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}
					>
						<div className="bg-white p-4 rounded-md text-sm">{lang === "en" ? content.en : content.sw}</div>
					</Accordion.Content>
				</Accordion.Item>
			</Accordion.Root>
		</div>
	);
}

export default RoleExplainerAccordion;
